// framework
import { immerable, produce } from "immer";
// api
import * as Client from "../../../../api/Client";
import { CallResultError } from "../../../../api/callHelper";

export interface IRootViewModel {
    readonly titleId: string | undefined;
    readonly titleNumber: string | undefined;
    readonly titleType: Client.TitleTypeEnum | undefined;
    readonly titleTypeName: string | undefined;
    readonly title2SubType: Client.Title2SubTypeEnum | undefined;
    readonly title2SubTypeName: string | undefined;
    readonly summary: Client.GetPublicTitleDetailsSummaryDto | undefined;
    readonly titleholders: Client.GetPublicTitleDetailsTitleholderDto[];
    readonly workProgram: Client.GetPublicTitleDetailsWorkProgramDto | undefined;
    readonly memorial: Client.GetPublicTitleDetailsMemorialApplicationDto[];
    readonly blocksAndLocations: Client.GetPublicTitleDetailsBlocksAndLocationsDto | undefined;
    readonly map: Client.GetPublicTitleDetailsMapResponseDto | undefined;
    readonly isBusy: boolean;
    readonly error: CallResultError | undefined;

    titleDetailsLoading(): IRootViewModel;
    titleDetailsLoaded(response: Client.GetPublicTitleDetailsResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
    mapLoading(): IRootViewModel;
    mapLoaded(response: Client.GetPublicTitleDetailsMapResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
}

export class RootViewModel implements IRootViewModel {
    [immerable] = true;

    constructor() {
        this.titleId = undefined;
        this.titleholders = [];
        this.memorial = [];
        this.isBusy = false;
        this.error = undefined;
    }
    public readonly titleId: string | undefined;
    public readonly titleNumber: string | undefined;
    public readonly titleType: Client.TitleTypeEnum | undefined;
    public readonly titleTypeName: string | undefined;
    public readonly title2SubType: Client.Title2SubTypeEnum | undefined;
    public readonly title2SubTypeName: string | undefined;
    public readonly summary: Client.GetPublicTitleDetailsSummaryDto | undefined;
    public readonly titleholders: Client.GetPublicTitleDetailsTitleholderDto[];
    public readonly workProgram: Client.GetPublicTitleDetailsWorkProgramDto | undefined;
    public readonly memorial: Client.GetPublicTitleDetailsMemorialApplicationDto[];
    public readonly blocksAndLocations: Client.GetPublicTitleDetailsBlocksAndLocationsDto | undefined;
    public readonly map: Client.GetPublicTitleDetailsMapResponseDto | undefined;
    public readonly isBusy: boolean;
    public readonly error: CallResultError | undefined;

    public titleDetailsLoading(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public titleDetailsLoaded(response: Client.GetPublicTitleDetailsResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            if (response) {
                draft.titleId = response.titleId;
                draft.titleNumber = response.titleNumber;
                draft.titleType = response.titleType;
                draft.titleTypeName = response.titleTypeName;
                draft.title2SubType = response.title2SubType;
                draft.title2SubTypeName = response.title2SubTypeTypeName;
                draft.summary = response.summary;
                draft.titleholders = response.titleholders ?? [];
                draft.workProgram = response.workProgram;
                draft.memorial = response.memorial?.applications ?? [];
                draft.blocksAndLocations = response.blocksAndLocations;
            }
            draft.isBusy = false;
            draft.error = error;
        });
    }

    // don't show busy state, as it's slow and not the main part of the screen
    public mapLoading(): IRootViewModel {
        return produce(this, (draft) => {
            draft.map = undefined;
        });
    }

    public mapLoaded(response: Client.GetPublicTitleDetailsMapResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            draft.map = response;
            draft.error = error;
        });
    }
}

interface IViewConfiguration {
    readonly isWorkProgramVisible: boolean;
    readonly isBlocksAndLocationsTabVisible: boolean;
    readonly isLocationsDataVisible: boolean;
}

export class ViewConfiguration implements IViewConfiguration {
    public constructor(titleType: Client.TitleTypeEnum | undefined) {
        if (!titleType) {
            this.isWorkProgramVisible = false;
            this.isBlocksAndLocationsTabVisible = false;
            this.isLocationsDataVisible = false;
            return;
        }

        switch (titleType) {
            case Client.TitleTypeEnum.AccessAuthority:
                this.isWorkProgramVisible = false;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.ExplorationPermit:
                this.isWorkProgramVisible = true;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = true;
                return;
            case Client.TitleTypeEnum.InfrastructureLicence:
                this.isWorkProgramVisible = false;
                this.isBlocksAndLocationsTabVisible = false;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.PipelineLicence:
                this.isWorkProgramVisible = false;
                this.isBlocksAndLocationsTabVisible = false;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.ProductionLicence:
                this.isWorkProgramVisible = false;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.RetentionLease:
                this.isWorkProgramVisible = true;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.SpecialProspectingAuthority:
                this.isWorkProgramVisible = false;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = false;
                return;
            case Client.TitleTypeEnum.GreenhouseGasAssessmentPermit:
                this.isWorkProgramVisible = true;
                this.isBlocksAndLocationsTabVisible = true;
                this.isLocationsDataVisible = false;
                return;
            default:
                throw new Error(`Unsupported Title Type '${titleType}'.`);
        }
    }

    public readonly isWorkProgramVisible: boolean;
    public readonly isBlocksAndLocationsTabVisible: boolean;
    public readonly isLocationsDataVisible: boolean;
}
